import { useMatches } from "@tanstack/react-router";
import { useEffect } from "react";

import type { ProjectHierarchy } from "@/openapi";
import { m } from "@/paraglide/messages";
import type { FileRouteTypes } from "@/routeTree.gen";

const BRAND = "CynthAI";
const SEPARATOR = " - ";

type RouteId = FileRouteTypes["id"];

// the deepest matched route with an entry names the page, so a layout entry
// covers every child route that has none
const pageTitles: Partial<Record<RouteId, () => string>> = {
  "/auth/sign-in": m.sign_in,
  "/auth/workos/callback": m.sign_in,
  "/auth/sign-up": m.page_title_sign_up,
  "/auth/forgot-password": m.page_title_forgot_password,
  "/auth/change-password": m.page_title_change_password,
  "/auth/two-fa": m.page_title_two_factor,
  "/auth/two-fa-verify": m.page_title_two_factor,
  "/auth/verify": m.page_title_verify_email,
  "/auth/request-verify": m.page_title_verify_email,
  "/auth/terms": m.page_title_terms,

  "/_protected/": m.page_title_chat,
  "/_protected/history/": m.chat_history,
  "/_protected/bi/": m.dashboard,
  "/_protected/settings/": m.settings,
  "/_protected/report-issue/": m.page_title_report_issue,
  "/_protected/not-authorized/": m.page_title_not_authorized,
  "/_protected/no-organization/": m.page_title_no_organization,

  "/_protected/data": m.data_management,
  "/_protected/data/collections": m.page_title_collections,
  "/_protected/data/databases": m.page_title_databases,
  "/_protected/data/datasources": m.page_title_datasources,

  "/_protected/workflows": m.workflows,
  "/_protected/workflows/results": m.page_title_workflow_results,
  "/_protected/workflows/schedule": m.page_title_workflow_schedule,
  "/_protected/workflows_/run/": m.page_title_run_workflow,
  "/_protected/workflow-editor/": m.auditor_workflow_editor,

  "/_protected/template-editor/": m.auditor_template_editor,
  "/_protected/template-editor/statement-trees": m.auditor_template_editor,
  "/_protected/template-editor/statement-trees/new":
    m.page_title_new_statement_tree,
  "/_protected/template-editor/classification-templates/create/":
    m.page_title_new_classification_template,
  "/_protected/template-editor/classification-templates/$templateId/create-version":
    m.page_title_new_template_version,

  "/_protected/document-analysis/": m.document_analysis,
  "/_protected/document-analysis/create/": m.page_title_new_document_analysis,
  "/_protected/document-analysis/results":
    m.page_title_document_analysis_results,
  "/_protected/document-analysis/bulk-results":
    m.page_title_document_analysis_results,
  "/_protected/document-analysis/comparison": m.page_title_document_comparison,

  "/_protected/help-center/": m.help_center,
  "/_protected/help-center/search/": m.page_title_help_center_search,
  "/_protected/help-center/faq/": m.faqs,
  "/_protected/help-center/articles/": m.page_title_articles,
  "/_protected/help-center/articles/$id/": m.page_title_articles,
  "/_protected/help-center/articles/new": m.page_title_new_article,
  "/_protected/help-center/articles/$id/edit": m.page_title_edit_article,
  "/_protected/help-center/release-notes/": m.release_notes,
  "/_protected/help-center/release-notes/$id/": m.release_notes,
  "/_protected/help-center/release-notes/new": m.page_title_new_release_notes,
  "/_protected/help-center/release-notes/$id/edit":
    m.page_title_edit_release_notes,
  "/_protected/help-center/admin": m.page_title_help_center_admin,

  "/_protected/organization/$orgId": m.page_title_organization,
  "/_protected/organization/$orgId/users/": m.page_title_users,
  "/_protected/organization/$orgId/departments/": m.page_title_departments,
  "/_protected/organization/$orgId/invitations/": m.page_title_invitations,
  "/_protected/organization/$orgId/training/": m.page_title_training,
  "/_protected/organization/$orgId/review": m.page_title_chat_review,
  "/_protected/organization/$orgId_/review/$sessionId":
    m.page_title_chat_review,
  "/_protected/organization/$orgId/user/$userId/": m.page_title_user,
  "/_protected/organization/$orgId_/department/$departmentId":
    m.page_title_department,
  "/_protected/organization/$orgId_/department/$departmentId/members":
    m.page_title_members,
  "/_protected/organization/$orgId_/department/$departmentId/review":
    m.page_title_chat_review,
  "/_protected/organization/$orgId_/department/$departmentId_/review/$sessionId":
    m.page_title_chat_review,

  "/_protected/superuser": m.admin_panel,
  "/_protected/superuser/customers": m.admin_customers,
  "/_protected/superuser/settings": m.settings,
  "/_protected/superuser/usage": m.admin_usage,
  "/_protected/superuser/usage/sessions": m.page_title_sessions,
  "/_protected/superuser/usage/conversations": m.page_title_conversations,
  "/_protected/superuser/usage_/sessions/$sessionId": m.page_title_session,
  "/_protected/superuser/user/$id": m.page_title_user,

  "/_protected/auditor/clients/": m.auditor_projects,
  "/_protected/auditor/validation/": m.validation_tests_title,
  "/_protected/auditor/validation/create": m.page_title_new_validation_test,
  "/_protected/auditor/validation/$id/": m.page_title_validation_test,
  "/_protected/auditor/clients/$id/_client-tabs/": m.project_tab_overview,
  "/_protected/auditor/clients/$id/_client-tabs/tasks": m.project_tab_tasks,
  "/_protected/auditor/clients/$id/_client-tabs/validations": m.data_validation,
  "/_protected/auditor/clients/$id/_client-tabs/document-analysis":
    m.project_tab_document_analysis,
  "/_protected/auditor/clients/$id/_client-tabs/financial-explorer":
    m.financial_explorer_tab,
  "/_protected/auditor/clients/$id/_client-tabs/agbw-overview":
    m.project_tab_agbw,
  "/_protected/auditor/clients/$id/_client-tabs/continuous-auditor-results":
    m.project_tab_continuous_auditor,
  "/_protected/auditor/clients/$id/_client-tabs/input-process":
    m.input_process_tab,
  "/_protected/auditor/clients/$id/_client-tabs/input-process/documents":
    m.input_process_step_documents,
  "/_protected/auditor/clients/$id/_client-tabs/input-process/trial-balance":
    m.input_process_step_trial_balance,
  "/_protected/auditor/clients/$id/_client-tabs/input-process/classification":
    m.input_process_step_classification,
  "/_protected/auditor/clients/$id/_client-tabs/input-process/statement-extraction":
    m.input_process_step_statement_extraction,
  "/_protected/auditor/clients/$id/_client-tabs/input-process/reconciliation":
    m.input_process_step_reconciliation,
  "/_protected/auditor/clients/$id/_agbw": m.project_tab_agbw,
  "/_protected/auditor/clients/$id/agbw-results": m.page_title_agbw_results,
  "/_protected/auditor/clients/$id/analysis/$rid": m.page_title_analysis,
  "/_protected/auditor/clients/$id/classifications/editor":
    m.page_title_classification_editor,
  "/_protected/auditor/clients/$id/classifications/$cls/":
    m.input_process_step_classification,

  "/_protected/corporate-finance/projects/": m.corporate_finance_projects,
  "/_protected/corporate-finance/validation/": m.validation_tests_title,
  "/_protected/corporate-finance/taxonomies/$taxonomyId": m.page_title_taxonomy,
  "/_protected/corporate-finance/projects/$id/_project-tabs/":
    m.project_tab_overview,
  "/_protected/corporate-finance/projects/$id/_project-tabs/workflows":
    m.project_tab_tasks,
  "/_protected/corporate-finance/projects/$id/_project-tabs/document-analysis":
    m.project_tab_document_analysis,
  "/_protected/corporate-finance/projects/$id/_project-tabs/financial-explorer":
    m.financial_explorer_tab,
  "/_protected/corporate-finance/projects/$id/_project-tabs/input-process":
    m.input_process_tab,
  "/_protected/corporate-finance/projects/$id/_project-tabs/input-process/documents":
    m.input_process_step_documents,
  "/_protected/corporate-finance/projects/$id/_project-tabs/input-process/classification":
    m.input_process_step_classification,
  "/_protected/corporate-finance/projects/$id/_project-tabs/input-process/statement-extraction":
    m.input_process_step_statement_extraction,
  "/_protected/corporate-finance/projects/$id/_project-tabs/input-process/reconciliation":
    m.input_process_step_reconciliation,
  "/_protected/corporate-finance/projects/$id/account-mapping":
    m.page_title_account_mapping,
  "/_protected/corporate-finance/projects/$id/analysis/$rid":
    m.page_title_analysis,

  "/_protected/fas/projects/": m.fas_projects,
  "/_protected/fas/taxonomies/$taxonomyId": m.page_title_taxonomy,
  "/_protected/fas/projects/$id/_project-tabs/": m.project_tab_overview,
  "/_protected/fas/projects/$id/_project-tabs/workflows": m.project_tab_tasks,
  "/_protected/fas/projects/$id/_project-tabs/financial-explorer":
    m.financial_explorer_tab,
};

interface TitleMatch {
  routeId: string;
  context: object;
}

function hasHierarchy(
  context: object,
): context is { hierarchy: ProjectHierarchy } {
  return "hierarchy" in context;
}

export function buildDocumentTitle(matches: readonly TitleMatch[]): string {
  const page = matches.findLast((match) => match.routeId in pageTitles);
  const project = matches.map((match) => match.context).findLast(hasHierarchy);

  return [
    project?.hierarchy.root.name,
    page ? pageTitles[page.routeId as RouteId]?.() : undefined,
    BRAND,
  ]
    .filter(Boolean)
    .join(SEPARATOR);
}

export function useDocumentTitle(): void {
  const title = useMatches({ select: buildDocumentTitle });

  useEffect(() => {
    document.title = title;
  }, [title]);
}
